import { useEffect, useMemo, useState } from 'react';
import { api, formatErr } from '@/lib/api';
import { PageHeader, PageShell, Stat } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Play, Square, Coffee, Utensils, Pencil, ChevronLeft, ChevronRight } from 'lucide-react';
import AttendanceHeatmap from '@/components/AttendanceHeatmap';

function monthBounds(monthStr /* YYYY-MM */) {
  const [y, m] = monthStr.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0); // day 0 = last day of previous month
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: iso(first), to: iso(last), label: first.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) };
}

function shiftMonth(monthStr, dir) {
  const [y, m] = monthStr.split('-').map(Number);
  const d = new Date(y, m - 1 + dir, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function toLocalInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}

function fromLocalInput(v) {
  if (!v) return null;
  const d = new Date(v);
  return d.toISOString();
}

const STATUS_BADGE = {
  full_day: { l: 'Full day', c: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
  half_day: { l: 'Half day', c: 'bg-amber-500/15 text-amber-600 border-amber-500/30' },
};

export default function Attendance() {
  const { user } = useAuth();
  const canEdit = ['admin', 'manager'].includes(user?.role);
  const [today, setToday] = useState(null);
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');

  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const bounds = useMemo(() => monthBounds(month), [month]);
  const [from, setFrom] = useState(bounds.from);
  const [to, setTo] = useState(bounds.to);
  useEffect(() => { setFrom(bounds.from); setTo(bounds.to); }, [bounds.from, bounds.to]);

  const [editRow, setEditRow] = useState(null);

  const load = async () => {
    try {
      const params = { from_date: from, to_date: to };
      if (user.role !== 'hr' && filter !== 'all' && filter !== 'me') params.user_id = filter;
      const [t, list] = await Promise.all([
        api.get('/attendance/today'),
        api.get('/attendance', { params }),
      ]);
      setToday(t.data); setRows(list.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, from, to]);
  useEffect(() => {
    if (user.role !== 'hr') api.get('/users').then(({ data }) => setUsers(data)).catch(() => {});
  }, [user.role]);

  const call = async (path) => {
    try { await api.post(path, path.endsWith('punch-in') ? {} : {}); toast.success('Done'); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const totalHours = rows.reduce((s, r) => s + (r.hours || 0), 0);
  const daysFull = rows.filter(r => r.status === 'full_day').length;
  const daysHalf = rows.filter(r => r.status === 'half_day').length;

  // Today state derivations
  const t = today;
  const lunchActive = !!(t?.lunch_start && !t?.lunch_end);
  const canLunchStart = !!t?.punch_in && !t?.punch_out && !t?.lunch_start;
  const canPunchOut = !!t?.punch_in && !t?.punch_out;

  const inTime = t?.punch_in ? new Date(t.punch_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
  const outTime = t?.punch_out ? new Date(t.punch_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
  const lunchStr = t?.lunch_start
    ? `${new Date(t.lunch_start).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}${t.lunch_end ? ' → ' + new Date(t.lunch_end).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ' · ongoing'}${t.lunch_minutes ? ` (${t.lunch_minutes}m)` : ''}`
    : '—';

  return (
    <PageShell>
      <PageHeader
        eyebrow="Time tracking"
        title="Attendance"
        subtitle="Punch in, take your lunch break, then punch out. Full-day = arrived before 3 PM, stayed past 4 PM and clocked at least 8h 45m."
        actions={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {!t?.punch_in && (
              <Button data-testid="punch-in-btn" onClick={() => call('/attendance/punch-in')} className="bg-primary hover:bg-primary/90 text-white"><Play className="w-4 h-4 mr-2" />Punch In</Button>
            )}
            {t?.punch_in && !t?.punch_out && (
              <>
                {canLunchStart && (
                  <Button data-testid="lunch-start-btn" variant="outline" onClick={() => call('/attendance/lunch-start')} className="border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white"><Utensils className="w-4 h-4 mr-2" />Start Lunch</Button>
                )}
                {lunchActive && (
                  <Button data-testid="lunch-end-btn" variant="outline" onClick={() => call('/attendance/lunch-end')} className="border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white"><Coffee className="w-4 h-4 mr-2" />End Lunch</Button>
                )}
                {canPunchOut && (
                  <Button data-testid="punch-out-btn" onClick={() => call('/attendance/punch-out')} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white"><Square className="w-4 h-4 mr-2" />Punch Out</Button>
                )}
              </>
            )}
            {t?.punch_out && (
              <div className="text-sm text-muted-foreground">
                Today: <span className="font-display font-black text-foreground">{t.hours}h</span>
                {t.status && <Badge variant="outline" className={`ml-2 ${STATUS_BADGE[t.status]?.c}`}>{STATUS_BADGE[t.status]?.l}</Badge>}
              </div>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Stat testid="att-full" label="Full Days" value={daysFull} accent />
        <Stat testid="att-half" label="Half Days" value={daysHalf} />
        <Stat testid="att-hours" label="Total Hours" value={totalHours.toFixed(1)} hint={bounds.label} />
        <Stat testid="att-today-in" label="Today · In" value={inTime} />
        <Stat testid="att-today-out" label="Today · Out" value={outTime} hint={t?.lunch_start ? `Lunch: ${lunchStr}` : ''} />
      </div>

      <Tabs defaultValue="heatmap" className="w-full">
        <TabsList data-testid="att-tabs">
          <TabsTrigger value="heatmap" data-testid="tab-heatmap">Team Heatmap</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">Punch Log</TabsTrigger>
        </TabsList>

        <TabsContent value="heatmap" className="mt-4">
          <AttendanceHeatmap />
        </TabsContent>

        <TabsContent value="log" className="mt-4">
          <div className="tactical-card p-3 mb-4 flex flex-wrap items-center gap-3">
            {/* Month cycle picker */}
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="month-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <div data-testid="month-label" className="px-3 font-display font-black min-w-[140px] text-center">{bounds.label}</div>
              <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="month-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>

            <div className="flex items-center gap-2 ml-2">
              <Label className="text-[10px] uppercase tracking-widest">From</Label>
              <Input data-testid="att-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40 h-9" />
              <Label className="text-[10px] uppercase tracking-widest">To</Label>
              <Input data-testid="att-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40 h-9" />
            </div>

            {user.role !== 'hr' && (
              <div className="ml-auto flex items-center gap-2">
                <Label className="text-[10px] uppercase tracking-widest">Recruiter</Label>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-56 h-9" data-testid="att-user-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All recruiters</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="tactical-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Punch In</TableHead>
                  <TableHead>Punch Out</TableHead>
                  <TableHead>Lunch</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  {canEdit && <TableHead className="text-right">Edit</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody data-testid="att-table-body">
                {rows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{new Date(r.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</TableCell>
                    <TableCell>{r.user_name}</TableCell>
                    <TableCell>{r.punch_in ? new Date(r.punch_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</TableCell>
                    <TableCell>{r.punch_out ? new Date(r.punch_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.lunch_minutes ? `${r.lunch_minutes}m` : '—'}</TableCell>
                    <TableCell>{r.status ? <Badge variant="outline" className={STATUS_BADGE[r.status]?.c}>{STATUS_BADGE[r.status]?.l}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-right font-display font-black">{r.hours ?? '—'}</TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => setEditRow(r)} data-testid={`edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={canEdit ? 8 : 7} className="text-center text-muted-foreground py-8">No records in this range.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <EditAttendanceDialog row={editRow} onClose={() => setEditRow(null)} onSaved={load} />
    </PageShell>
  );
}

function EditAttendanceDialog({ row, onClose, onSaved }) {
  const [form, setForm] = useState({ punch_in: '', punch_out: '', lunch_start: '', lunch_end: '', lunch_minutes: 0, reason: '' });
  useEffect(() => {
    if (!row) return;
    setForm({
      punch_in: toLocalInput(row.punch_in),
      punch_out: toLocalInput(row.punch_out),
      lunch_start: toLocalInput(row.lunch_start),
      lunch_end: toLocalInput(row.lunch_end),
      lunch_minutes: row.lunch_minutes || 0,
      reason: '',
    });
  }, [row]);

  if (!row) return null;

  const save = async () => {
    if (!form.reason.trim()) { toast.error('Reason is required'); return; }
    const payload = {
      punch_in: form.punch_in ? fromLocalInput(form.punch_in) : null,
      punch_out: form.punch_out ? fromLocalInput(form.punch_out) : null,
      lunch_start: form.lunch_start ? fromLocalInput(form.lunch_start) : null,
      lunch_end: form.lunch_end ? fromLocalInput(form.lunch_end) : null,
      lunch_minutes: parseInt(form.lunch_minutes || 0, 10),
      reason: form.reason.trim(),
    };
    try {
      await api.patch(`/attendance/${row.id}`, payload);
      toast.success('Attendance updated');
      onClose(); onSaved();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="edit-att-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black">
            Edit attendance <span className="text-muted-foreground font-normal text-base">· {row.user_name} · {row.date}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Punch In</Label><Input data-testid="edit-pi" type="datetime-local" value={form.punch_in} onChange={(e) => setForm({ ...form, punch_in: e.target.value })} /></div>
          <div><Label>Punch Out</Label><Input data-testid="edit-po" type="datetime-local" value={form.punch_out} onChange={(e) => setForm({ ...form, punch_out: e.target.value })} /></div>
          <div><Label>Lunch Start</Label><Input type="datetime-local" value={form.lunch_start} onChange={(e) => setForm({ ...form, lunch_start: e.target.value })} /></div>
          <div><Label>Lunch End</Label><Input type="datetime-local" value={form.lunch_end} onChange={(e) => setForm({ ...form, lunch_end: e.target.value })} /></div>
          <div><Label>Lunch minutes</Label><Input type="number" min="0" value={form.lunch_minutes} onChange={(e) => setForm({ ...form, lunch_minutes: e.target.value })} /></div>
          <div className="col-span-2"><Label>Reason for edit *</Label><Textarea data-testid="edit-reason" rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Wrong punch, mistake correction, etc." /></div>
          <Button data-testid="edit-save" onClick={save} className="col-span-2 bg-primary hover:bg-primary/90 text-white">Save</Button>
        </div>
        {row.edit_history && row.edit_history.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border/70">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Edit history</div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {row.edit_history.map((h, i) => (
                <div key={i} className="text-xs bg-muted/40 rounded p-2">
                  <div className="font-medium">{h.by_name} · {new Date(h.at).toLocaleString('en-IN')}</div>
                  <div className="text-muted-foreground">{h.reason}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
